import { useAsyncData, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import type { NavigationItem } from '../types'
import { SEASONAL_HIGHLIGHT_COLOR, mapMenuItem } from '../utils/mapMenuItem'
import { isBlackFridaySeason, isHolidaysSeason } from '../utils/seasonsHelpers'

import t from './useFetchNavigationItems.translations'

export function useFetchNavigationItems() {
  const $httpFetch = useHttpFetch()
  const i18n = useI18n()
  const locale = useI18nLocale().toLowerCase()

  const { data: mappedItems } = useAsyncData(
    'navigation-main-menu',
    () => $httpFetch('/bm/navigation/main'),
    {
      dedupe: 'defer',
      default: (): NavigationItem[] => [],
      transform: (data) => data.items.map((item) => mapMenuItem(item, locale)),
    },
  )

  const {
    features: {
      goodDeals: { enabled: isGoodDealsEnabled } = { enabled: false },
      blackFriday: { enabled: isBlackFridayEnabled } = { enabled: false },
      holidays: { enabled: isHolidaysEnabled } = { enabled: false },
    } = {},
  }: {
    features?: {
      goodDeals?: { enabled: boolean }
      blackFriday?: { enabled: boolean }
      holidays?: { enabled: boolean }
    }
  } = useMarketplace() ?? {}

  const { FF_ENABLE_NAVIGATION_ITEMS_BY_SERVICE = false } =
    useRuntimeConfig().public

  const items = computed(() => {
    const GOOD_DEALS: NavigationItem = {
      title: i18n(t.goodDeals),
      children: [],
      id: 'good-deals',
      color: SEASONAL_HIGHLIGHT_COLOR,
      icon: 'Sparkles',
      url: `/${locale}/e/good-deals`,
    }

    const BLACK_FRIDAY: NavigationItem = {
      title: 'Black Friday',
      children: [],
      id: 'black-friday',
      color: SEASONAL_HIGHLIGHT_COLOR,
      icon: 'Sparkles',
      url: `/${locale}/e/black-friday`,
    }

    const HOLIDAYS: NavigationItem = {
      title: i18n(t.holidays),
      children: [],
      id: 'holidays',
      color: SEASONAL_HIGHLIGHT_COLOR,
      icon: 'Sparkles',
      url: `/${locale}/e/holidays`,
    }

    if (FF_ENABLE_NAVIGATION_ITEMS_BY_SERVICE) {
      return mappedItems.value
    }

    return [GOOD_DEALS, BLACK_FRIDAY, HOLIDAYS]
      .concat(mappedItems.value)
      .filter((item) => {
        const now = new Date()
        if (item.id === 'black-friday') {
          return (
            isGoodDealsEnabled &&
            isBlackFridayEnabled &&
            isBlackFridaySeason(now)
          )
        }
        if (item.id === 'holidays') {
          return (
            isGoodDealsEnabled && isHolidaysEnabled && isHolidaysSeason(now)
          )
        }
        if (item.id === 'good-deals') {
          return (
            isGoodDealsEnabled &&
            !isHolidaysSeason(now) &&
            !isBlackFridaySeason(now)
          )
        }

        return true
      })
  })

  return { items }
}
